/**
 * Whether `url`'s real server actually answered -- not just whether some
 * network interface is up. Uses normal (not no-cors) mode: the backend
 * already sends `Access-Control-Allow-Origin: *`, so a resolved response
 * proves we reached it, even a non-2xx one (many GraphQL hosts don't
 * support HEAD and answer 400/405 -- still proof of reachability, not
 * proof of failure). A captive portal's injected page won't have the
 * right CORS headers for our origin, so it makes the fetch reject rather
 * than resolve as an indistinguishable "opaque" success the way a
 * no-cors request would -- opaque responses can't be inspected at all,
 * so a no-cors check can't tell a captive portal from the real host, and
 * treats a hard network failure as the only "offline" signal. Bounded by
 * an explicit timeout so a hanging/degraded connection can't leave
 * "am I online" unresolved indefinitely.
 *
 * Deliberately a plain, stateless function in a plain module: it answers
 * when asked and remembers nothing. `useIsOnline` is the one place that
 * holds an online/offline *value*, as React state, and `app/app.tsx`
 * publishes that on `AppContext` (`onlineStatus`) for everything that
 * needs it. Route `loader`s don't -- see `issueOptions`' `isOnline` note
 * in `shared/api/queries.ts`.
 */
export async function isReachable(
	url: string,
	timeoutMs = 5000,
): Promise<boolean> {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
	try {
		await fetch(url, { method: 'HEAD', signal: controller.signal });
		return true;
	} catch {
		return false;
	} finally {
		clearTimeout(timeoutId);
	}
}
